import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { avviaScadenzaInattivita } from '../lib/sessione'

/** Minuti di inattivita oltre i quali la sessione si chiude. */
const INATTIVITA_MINUTI = 30

interface Auth {
  sessione: Session | null
  caricamento: boolean
  accedi: (email: string, password: string) => Promise<void>
  esci: () => Promise<void>
  reimpostaPassword: (email: string) => Promise<void>
}

const Ctx = createContext<Auth | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [sessione, setSessione] = useState<Session | null>(null)
  const [caricamento, setCaricamento] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSessione(data.session ?? null)
      setCaricamento(false)
    })
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setSessione(s))
    return () => data.subscription.unsubscribe()
  }, [])

  useEffect(() => {
    if (!sessione) return
    return avviaScadenzaInattivita(INATTIVITA_MINUTI, () => {
      void supabase.auth.signOut()
    })
  }, [sessione])

  const valore = useMemo<Auth>(
    () => ({
      sessione,
      caricamento,
      accedi: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw new Error('Credenziali non valide')
      },
      esci: async () => {
        await supabase.auth.signOut()
      },
      reimpostaPassword: async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reimposta-password`,
        })
        if (error) throw new Error('Impossibile inviare il messaggio di reimpostazione')
      },
    }),
    [sessione, caricamento],
  )

  return <Ctx.Provider value={valore}>{children}</Ctx.Provider>
}

export function useAuth(): Auth {
  const c = useContext(Ctx)
  if (!c) throw new Error('useAuth va usato dentro AuthProvider')
  return c
}
